import { useEffect, useRef, useState, lazy, Suspense, ComponentType } from "react";
import Header from "@/components/Header";
import Hero, { MobileCTA } from "@/components/Hero";
import TrustedBy from "@/components/TrustedBy";

// Lazy load below-fold sections
const HowItWorks = lazy(() => import("@/components/HowItWorks"));
const HookSection = lazy(() => import("@/components/HookSection"));
const ProblemSection = lazy(() => import("@/components/ProblemSection"));
const Testimonials = lazy(() => import("@/components/Testimonials"));
const UKWideSection = lazy(() => import("@/components/UKWideSection"));
const VerticalsSection = lazy(() => import("@/components/VerticalsSection"));
const CaseStudies = lazy(() => import("@/components/CaseStudies"));
const TeamSection = lazy(() => import("@/components/TeamSection"));
const FAQSection = lazy(() => import("@/components/FAQSection"));
const FinalCTA = lazy(() => import("@/components/FinalCTA"));
const Footer = lazy(() => import("@/components/Footer"));

// Render children only once the sentinel scrolls within `rootMargin` of viewport.
// This keeps below-fold JS chunks out of the initial critical path.
const Defer = ({
  children,
  minHeight = 400,
  rootMargin = "800px",
}: {
  children: React.ReactNode;
  minHeight?: number;
  rootMargin?: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShow(true);
          io.disconnect();
        }
      },
      { rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [show, rootMargin]);

  return (
    <div ref={ref} style={!show ? { minHeight } : undefined}>
      {show ? <Suspense fallback={null}>{children}</Suspense> : null}
    </div>
  );
};

const Index = () => {
  // Scroll reveal
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll(".rv, .rv-l, .rv-r, .rv-s").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <Hero />
      <TrustedBy />
      {/* HowItWorks is right under the fold — mount immediately but lazily */}
      <Suspense fallback={<div style={{ minHeight: 400 }} />}>
        <HowItWorks />
      </Suspense>
      <Defer minHeight={500}>
        <HookSection />
      </Defer>
      <Defer minHeight={600}>
        <ProblemSection />
      </Defer>
      <Defer minHeight={500}>
        <Testimonials />
      </Defer>
      <Defer minHeight={600}>
        <UKWideSection />
      </Defer>
      <Defer minHeight={500}>
        <VerticalsSection />
      </Defer>
      <Defer minHeight={600}>
        <CaseStudies />
      </Defer>
      <Defer minHeight={500}>
        <TeamSection />
      </Defer>
      <Defer minHeight={500}>
        <FAQSection />
      </Defer>
      <Defer minHeight={500}>
        <FinalCTA />
      </Defer>
      <Defer minHeight={300}>
        <Footer />
      </Defer>
      <MobileCTA />
    </div>
  );
};

export default Index;
